import FooterWidget from 'nucleus-widgets/lib/Footer/FooterWidget';
import { connect } from 'react-redux';
import { getPlannerPrivacyPolicyLink, getPrivacyPolicyLink as getPrivacyPolicyURL } from '../../utils/privacyPolicyUtils';
import {
  getCcpaConfirmationText, getCcpaExplanationText, getCcpaInviteeValidationText, getCcpaLinkText,
  getCcpaQualifyingQuestionFailureText, getCcpaQualifyingQuestionFlag, getCcpaQualifyingQuestionText,
  getCventPrivacyPolicyLinkText, getShowCcpaSectionFlag, isComplianceSnapshotMigrated, shouldShowCventPrivacyPolicy
} from 'event-widgets/redux/selectors/complianceSettings';
import React from 'react';
import PropTypes from 'prop-types';
import BaseWidget from 'nucleus-widgets/lib/BaseWidget';
import { getIn } from 'icepick';
import { defaultMemoize } from 'reselect';
import CCPAOptOutDialog from './CCPAOptOutDialog';
import CCPADialogContent from './CCPADialogContent';
import FooterWidgetStyles from './FooterWidget.less';
import { CCPA_OPTOUT_OPTIONS, DIALOG_MODE } from '../../utils/ccpaUtils.js';
import renderRichText from 'nucleus-widgets/utils/renderRichText';
import { eventHasMultipleLanguages } from 'event-widgets/utils/multiLanguageUtils';

const dialogStyle = defaultMemoize((globalTheme, style) => {
  return {
    ...globalTheme,
    ...style,
    button: { ...style.elements.primaryButton, styleMapping: 'primaryButton' },
    input: { ...style.elements.input, styleMapping: 'input' },
    label: { ...style.elements.label, styleMapping: 'label' },
    body1: { ...style.elements.body1, styleMapping: 'body1' },
    header: globalTheme.dialog ? { ...globalTheme.dialog.header } :
      { styleMapping: 'header3' },
    dragHandle: globalTheme.dialog ? { ...globalTheme.dialog.header, spacing: { padding: 0 } } :
      { styleMapping: 'header3' },
    headerText: globalTheme.dialog ? globalTheme.dialog.headerText : { styleMapping: 'header2' },
    messageContainer: globalTheme.dialog ? globalTheme.dialog.body : {}
  };
});

function getLocalizationsFromTheState(state) {
  const currentLocale = state?.localizedUserText?.currentLocale;
  return state?.localizedUserText?.localizations[currentLocale];
}

function isMultiLanguageEventCheck(localizations, event) {
  return !!(eventHasMultipleLanguages(event)
    && localizations && Object.keys(localizations).length !== 0);
}

function getCcpaSettingsFromSnapshot(state) {
  return {
    showComplianceText: getShowCcpaSectionFlag(state),
    complianceText: getCcpaLinkText(state),
    showQualifyingQuestion: getCcpaQualifyingQuestionFlag(state),
    qualifyingQuestionText: getCcpaQualifyingQuestionText(state),
    qualifyingErrorText: getCcpaQualifyingQuestionFailureText(state),
    successfulOptOutText: getCcpaConfirmationText(state),
    unregisteredForEventText: getCcpaInviteeValidationText(state),
    instructionalText: getCcpaExplanationText(state)
  };
}

function getCcpaSettingsFromWebsite(ownProps, state, ccpaConfig) {
  const localizations = getLocalizationsFromTheState(state);
  const isMultiLanguageEvent = isMultiLanguageEventCheck(localizations, state.event);
  return {
    showComplianceText: ccpaConfig.visible,
    complianceText: isMultiLanguageEvent ?
      localizations['website.layoutItems.CventFooterSection.config.ccpa.linkText'] : ccpaConfig.linkText,
    showQualifyingQuestion: ccpaConfig.qualifyingQuestion.visible,
    qualifyingQuestionText: isMultiLanguageEvent ?
      localizations['website.layoutItems.CventFooterSection.config.ccpa.qualifyingQuestion.text']
      : ccpaConfig.qualifyingQuestion.text,
    qualifyingErrorText: isMultiLanguageEvent ?
      localizations['website.layoutItems.CventFooterSection.config.ccpa.qualifyingQuestion.errorText']
      : ccpaConfig.qualifyingQuestion.errorText,
    successfulOptOutText: isMultiLanguageEvent ?
      localizations['website.layoutItems.CventFooterSection.config.ccpa.successfulOptOutText']
      : ccpaConfig.successfulOptOutText,
    unregisteredForEventText: isMultiLanguageEvent ?
      localizations['website.layoutItems.CventFooterSection.config.ccpa.unregisteredForEventText']
      : ccpaConfig.unregisteredForEventText,
    instructionalText: isMultiLanguageEvent &&
    localizations['website.layoutItems.CventFooterSection.config.ccpa.content.blocks[0].text'] ?
      renderRichText(localizations['website.layoutItems.CventFooterSection.config.ccpa.content.blocks[0].text'],
        ownProps.translate, ownProps.style.palette)
      : ccpaConfig.instructionalText && renderRichText(ccpaConfig.instructionalText,
        ownProps.translate, ownProps.style.palette)
  };
}

export function getCcpaConfig(ownProps, state) {
  if ((state.experiments ? state.experiments.isMovePrivacyComplianceFromSiteEditorEnabled :
    state.experimentSettings?.isMovePrivacyComplianceFromSiteEditorEnabled) && isComplianceSnapshotMigrated(state)) {
    return getCcpaSettingsFromSnapshot(state);
  }

  const ccpaConfig = state.website.layoutItems.CventFooterSection.config.ccpa;
  if (ccpaConfig) {
    return getCcpaSettingsFromWebsite(ownProps, state, ccpaConfig);
  }
  return {};
}

const getPlannerPrivacyPolicy = (links, config, isMultiLanguageEvent, localizations) => {
  if (config && config.plannerPrivacyPolicy && config.plannerPrivacyPolicy.visible) {
    links.push({
      url: isMultiLanguageEvent ? localizations['website.layoutItems.CventFooterSection.config.plannerPrivacyPolicy.url']
        : config.plannerPrivacyPolicy.url,
      text: isMultiLanguageEvent ? localizations['website.layoutItems.CventFooterSection.config.plannerPrivacyPolicy.text'] : config.plannerPrivacyPolicy.text
    });
  }
  return links;
};

export function getPrivacyPolicyLinks(state, config) {
  let links = [];
  if ((state.experiments ? state.experiments.isMovePrivacyComplianceFromSiteEditorEnabled :
    state.experimentSettings?.isMovePrivacyComplianceFromSiteEditorEnabled) && isComplianceSnapshotMigrated(state)) {
    if (shouldShowCventPrivacyPolicy(state)) {
      links.push({
        url: getPrivacyPolicyURL(state.event.languageId || state.event.cultureCode),
        text: getCventPrivacyPolicyLinkText(state)
      });
    }
    return links.concat(getPlannerPrivacyPolicyLink(state));
  }

  const localizations = getLocalizationsFromTheState(state);
  const isMultiLanguageEvent = isMultiLanguageEventCheck(localizations, state.event);
  if (config && config.cventPrivacyPolicy && config.cventPrivacyPolicy.visible) {
    links.push({
      url: getPrivacyPolicyURL(state.event.languageId || state.event.cultureCode),
      text: isMultiLanguageEvent ?
        localizations['website.layoutItems.CventFooterSection.config.cventPrivacyPolicy.text'] : config.cventPrivacyPolicy.text
    });
  }
  return getPlannerPrivacyPolicy(links, config, isMultiLanguageEvent, localizations);
}
// returns appropriated widget to be displayed on editor or guest side
export class EventFooterWidget extends BaseWidget {
  static displayName = 'EventFooterWidget';
  static propTypes = {
    allowCustomFooter: PropTypes.bool,
    showPrivacyPolicy: PropTypes.bool,
    additionalPolicyLinks: PropTypes.array,
    isOnGuestSide: PropTypes.object,
    primaryEventRegistrationInfo: PropTypes.object,
    isAttendeeRegistered: PropTypes.bool,
    guestsEventRegistrationsInfo: PropTypes.array,
    isAdminReg: PropTypes.bool,
    style: PropTypes.object,
    translate: PropTypes.func,
    ccpaDialogMode: PropTypes.string,
    CCPAConfig: PropTypes.object,
    switchCcpaDialogMode: PropTypes.func,
    makeCcpaComplianceRequest: PropTypes.func
  };

  constructor(props) {
    super(props);
    this.state = {
      showCcpaDialog: false,
      ccpaOptOutValue: '',
      ccpaGuestList: []
    };
  }

  openCcpaDialog = () => {
    const { isAdminReg, isAttendeeRegistered, CCPAConfig,
      ccpaDialogMode, switchCcpaDialogMode } = this.props;

    if (ccpaDialogMode === DIALOG_MODE.SUCCESS_OPT_OUT || ccpaDialogMode === DIALOG_MODE.FAILURE_OPT_OUT) {
      this.setState({ ccpaGuestList: [] }, () => {
        switchCcpaDialogMode(DIALOG_MODE.UNREGISTERED);
      });
    }
    this.setState({ showCcpaDialog: true }, () => {
      if (!isAttendeeRegistered) {
        switchCcpaDialogMode(DIALOG_MODE.UNREGISTERED);
      } else if (isAdminReg) {
        switchCcpaDialogMode(DIALOG_MODE.ADMIN_REG);
      } else if (CCPAConfig.showQualifyingQuestion) {
        switchCcpaDialogMode(DIALOG_MODE.SHOW_QUALIFYING_QUESTION);
      } else if (!CCPAConfig.showQualifyingQuestion) {
        switchCcpaDialogMode(DIALOG_MODE.SHOW_USER_INFO);
      }
    });
  };

  getCcpaComplianceRequest = () => {
    const { primaryEventRegistrationInfo, guestsEventRegistrationsInfo, isPlanner } = this.props;
    const { ccpaGuestList } = this.state;
    if (!primaryEventRegistrationInfo) {
      return [];
    }
    let ccpaComplianceRequests = [];
    ccpaComplianceRequests.push({
      firstName: primaryEventRegistrationInfo.firstName || '',
      lastName: primaryEventRegistrationInfo.lastName || '',
      email: primaryEventRegistrationInfo.emailAddress || '',
      attendeeEntityType: 'ATTENDEE',
      attendeeEntityId: primaryEventRegistrationInfo.attendeeEntityId,
      isCreatedByAttendee: !isPlanner
    });
    const ccpaOptOutGuests = ccpaGuestList && ccpaGuestList.filter(guest => guest.checked);
    if (ccpaOptOutGuests.length > 0) {
      ccpaOptOutGuests.forEach(optOutGuest => {
        let guestRegistration = guestsEventRegistrationsInfo
          .filter(guest => guest.attendeeEntityId === optOutGuest.value)[0];
        ccpaComplianceRequests.push({
          firstName: guestRegistration.firstName || '',
          lastName: guestRegistration.lastName || '',
          email: guestRegistration.emailAddress || '',
          attendeeEntityType: 'GUEST',
          attendeeEntityId: guestRegistration.attendeeEntityId,
          isCreatedByAttendee: !isPlanner
        });
      });
    }
    return ccpaComplianceRequests;
  };

  updateGuests = (guestList) => {
    this.setState({
      ccpaGuestList: guestList
    });
  };

  closeCcpaDialog = () => {
    this.setState({
      showCcpaDialog: false,
      ccpaGuestList: []
    }, () => this.props.switchCcpaDialogMode(DIALOG_MODE.UNREGISTERED));
  };

  handleDialogConfirmationClick = () => {
    const { ccpaOptOutValue } = this.state;
    const { makeCcpaComplianceRequest, ccpaDialogMode: dialogMode, switchCcpaDialogMode } = this.props;
    if (dialogMode === DIALOG_MODE.UNREGISTERED || dialogMode === DIALOG_MODE.ADMIN_REG ||
      dialogMode === DIALOG_MODE.SUCCESS_OPT_OUT || dialogMode === DIALOG_MODE.FAILURE_OPT_OUT ||
      dialogMode === DIALOG_MODE.SHOW_NO_CALAFORNIA_RESIDENT) {
      this.setState({ showCcpaDialog: false });
    } else if (dialogMode === DIALOG_MODE.SHOW_QUALIFYING_QUESTION &&
      ccpaOptOutValue === CCPA_OPTOUT_OPTIONS.YES) {
      switchCcpaDialogMode(DIALOG_MODE.SHOW_USER_INFO);
    } else if (dialogMode === DIALOG_MODE.SHOW_QUALIFYING_QUESTION &&
      ccpaOptOutValue === CCPA_OPTOUT_OPTIONS.NO) {
      switchCcpaDialogMode(DIALOG_MODE.SHOW_NO_CALAFORNIA_RESIDENT);
    } else if (dialogMode === DIALOG_MODE.SHOW_USER_INFO) {
      makeCcpaComplianceRequest(this.getCcpaComplianceRequest());
    }
  };

  setOptOutRadioOptions = (value) => {
    this.setState({ ccpaOptOutValue: value });
  };

  getStyleObject() {
    return {
      ...super.getStyleObject(),
      label: this.getElementInlineStyle('label'),
      input: this.getElementInlineStyle('input'),
      mainText: this.getElementInlineStyle('body1'),
      button: this.getElementInlineStyle('button'),
      header: this.getElementInlineStyle('header'),
      exit: this.getElementInlineStyle('headerText'),
      dragContainer: this.getElementInlineStyle('messageContainer'),
      headerFont: this.getElementInlineStyle('headerText')
    };
  }

  getCCPAOptOutModalContent(props, classes) {
    const { primaryEventRegistrationInfo, guestsEventRegistrationsInfo, isAttendeeRegistered,
      translate, qualifyingQuestionText, successfulOptOutText, qualifyingErrorText, complianceText,
      unregisteredForEventText, instructionalText, ccpaDialogMode: dialogMode } = props;

    const style = this.getStyleObject();

    const modalContentConfig = {
      translate,
      primaryEventRegistrationInfo,
      guestsEventRegistrationsInfo,
      isAttendeeRegistered,
      style,
      qualifyingQuestionText,
      qualifyingErrorText,
      successfulOptOutText,
      unregisteredForEventText,
      instructionalText,
      classes,
      updateGuests: this.updateGuests,
      setOptOutRadioOptions: this.setOptOutRadioOptions,
      handleDialogConfirmationClick: this.handleDialogConfirmationClick,
      dialogMode
    };

    const modalConfig = {
      isOpen: true,
      isModal: true,
      header: translate(complianceText),
      isFixedHeader: false,
      isFullScreen: false,
      translate,
      style,
      classes,
      handleDialogClose: this.closeCcpaDialog,
      updateGuests: this.updateGuests,
      children: (
        <CCPADialogContent
          {...modalContentConfig}>
        </CCPADialogContent>
      )
    };

    return (
      <CCPAOptOutDialog {...modalConfig}>
      </CCPAOptOutDialog>
    );
  }


  render() {
    const props = this.props;
    const classes = FooterWidgetStyles;
    if (props.allowCustomFooter && props.additionalPolicyLinks.length === 0 && !props.CCPAConfig.showComplianceText
      && props.isOnGuestSide) {
      return <div />;
    }
    let newProps = {
      ...props,
      ...props.CCPAConfig,
      onComplianceClick: this.openCcpaDialog
    };
    return (
      <>
        {
          this.state.showCcpaDialog && props.isOnGuestSide &&
          this.getCCPAOptOutModalContent(newProps, classes)
        }
        <FooterWidget {...newProps} />
      </>
    );
  }
}


/* Data wrapper for the footer widget */
export default connect((state, ownProps) => ({
  allowCustomFooter: state.account.settings.allowCustomEventFooter,
  showPrivacyPolicy: false,
  additionalPolicyLinks: getPrivacyPolicyLinks(state, state.website.layoutItems.CventFooterSection.config),
  isOnGuestSide: getIn(state, ['userSession']),
  primaryEventRegistrationInfo: ownProps.primaryEventRegistrationInfo,
  guestsEventRegistrationsInfo: ownProps.guestsEventRegistrationsInfo,
  isAttendeeRegistered: ownProps.isAttendeeRegistered,
  style: dialogStyle(state.website.theme.global, ownProps.style),
  translate: ownProps.translate,
  isAdminReg: ownProps.isAdminReg,
  CCPAConfig: getCcpaConfig(ownProps, state),
  makeCcpaComplianceRequest: ownProps.makeCcpaComplianceRequest,
  switchCcpaDialogMode: ownProps.switchCcpaDialogMode,
  ccpaDialogMode: ownProps.ccpaDialogMode
}))(EventFooterWidget);
